import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCities, getSuggestedDestinations, placeAutocomplete, placeDetails } from '../api';
import { useClickOutside, useDebounced } from '../hooks';
import { CategoryIcon, IconClock, IconHotel, IconMoon, IconNavigate, IconPin, IconSearch } from '../icons';
import { addDays, defaultSearch, formatTime, KINDS, nightsBetween, readLastSearch, rememberSearch, searchUrl, todayKey } from '../searchState';
import Calendar from './Calendar';
import { Stepper } from './ui';

/* 30-minute check-in slots for hourly stays, "HH:MM" */
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
const nowTime = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

const TABS = [
  { key: 'stays', label: 'Stays' },
  { key: 'spas', label: 'Spas' },
  { key: 'tours', label: 'Tours' },
  { key: 'packages', label: 'Packages' },
];

const dateParts = (key) => {
  if (!key) return { day: '--', month: '', weekday: '' };
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return {
    day: String(d),
    month: `${date.toLocaleDateString('en-IN', { month: 'short' })}'${String(y).slice(2)}`,
    weekday: date.toLocaleDateString('en-IN', { weekday: 'long' }),
  };
};

/**
 * The search card: product tabs on top, then "value-first" fields (a small
 * label and a big bold value, like the big booking sites), and an orange
 * Search button. `variant="hero"` on the home page; `compact` on list pages.
 */
const SearchBar = ({ variant = 'hero', kind: initialKind = 'stays', initial, onSearch, lockKind = false }) => {
  const navigate = useNavigate();
  const [kind, setKind] = useState(initialKind);
  const [search, setSearch] = useState(() => initial || readLastSearch() || defaultSearch());
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [typing, setTyping] = useState(false);
  const debounced = useDebounced(query, 280);
  const [predictions, setPredictions] = useState([]);
  const [cities, setCities] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [open, setOpen] = useState(null); // 'where' | 'in' | 'out' | 'time' | 'guests' | null
  const [locating, setLocating] = useState(false);
  const boxRef = useRef(null);
  const whereInput = useRef(null);
  const timePop = useRef(null);
  useClickOutside(boxRef, () => setOpen(null), open !== null);

  useEffect(() => { setKind(initialKind); }, [initialKind]);
  useEffect(() => { if (initial) setSearch(initial); }, [initial]);

  useEffect(() => {
    getCities().then((r) => setCities(r?.data || [])).catch(() => {});
    getSuggestedDestinations().then((r) => setDestinations(r?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!typing || debounced.trim().length < 3) { setPredictions([]); return undefined; }
    let cancelled = false;
    placeAutocomplete(debounced.trim())
      .then((r) => { if (!cancelled) setPredictions(r?.data || []); })
      .catch(() => { if (!cancelled) setPredictions([]); });
    return () => { cancelled = true; };
  }, [debounced, typing]);

  useEffect(() => {
    if (open === 'where') setTimeout(() => whereInput.current?.focus(), 30);
    // Open the time list at the chosen slot rather than at midnight
    if (open === 'time') setTimeout(() => timePop.current?.querySelector('.is-active')?.scrollIntoView({ block: 'center' }), 20);
  }, [open]);

  const cityMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return cities.filter((c) => c.toLowerCase().includes(q)).slice(0, 6);
  }, [cities, query]);

  const choose = (next) => {
    setSearch({ ...search, ...next });
    setOpen(null);
    setTyping(false);
    setQuery('');
  };

  const pickPlace = async (prediction) => {
    const description = prediction.description || '';
    try {
      const r = await placeDetails(prediction.place_id);
      const d = r?.data || {};
      const city = d.canonicalCity || d.city || d.state || description;
      const pickedName = (prediction.structured_formatting?.main_text || description.split(',')[0]).trim();
      const area = d.city && pickedName.toLowerCase() !== d.city.toLowerCase() ? pickedName : '';
      choose({ city, state: d.state || '', country: d.country || '', lat: d.latitude ?? null, lng: d.longitude ?? null, area, near: false });
    } catch (err) {
      choose({ city: description, state: '', country: '', lat: null, lng: null, area: '', near: false });
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError('Location is not available in this browser.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocating(false); choose({ city: 'Nearby', state: '', country: '', lat: pos.coords.latitude, lng: pos.coords.longitude, area: '', near: true }); },
      () => { setLocating(false); setError('Could not get your location. Please allow location access or type a city.'); },
      { timeout: 8000 }
    );
  };

  const submit = (event) => {
    if (event) event.preventDefault();
    setError('');
    if (!search.city) { setError('Where do you want to go? Pick a city from the list.'); setOpen('where'); return; }
    rememberSearch(search);
    if (onSearch) onSearch(kind, search);
    navigate(searchUrl(kind, search));
  };

  const isStays = kind === 'stays';
  const isHourly = isStays && search.stay === 'hourly';
  const nights = nightsBetween(search.checkIn, search.checkOut);
  const twoMonths = typeof window === 'undefined' || window.innerWidth >= 768;

  const pickCheckIn = (key) => {
    const checkOut = search.checkOut <= key ? addDays(key, 1) : search.checkOut;
    setSearch({ ...search, checkIn: key, checkOut });
    setOpen(isHourly ? null : 'out');
  };
  const pickCheckOut = (key) => {
    setSearch({ ...search, checkOut: key });
    setOpen(null);
  };
  const pickTime = (time) => {
    setSearch({ ...search, time });
    setOpen(null);
  };
  const setStay = (stay) => {
    setSearch({ ...search, stay });
    setOpen(null);
  };
  const guests = search.adults + search.children;
  const inParts = dateParts(search.checkIn);
  const outParts = dateParts(search.checkOut);
  const whereMain = search.near ? 'Near me' : search.area || (search.city ? search.city.split(',')[0] : '');
  const whereSub = search.near ? 'Your current location' : search.area ? search.city : search.city ? search.city.split(',').slice(1).join(',').trim() || 'India' : 'City, area or landmark';

  return (
    <form className={`gb-search gb-search-${variant}`} onSubmit={submit} ref={boxRef}>
      {!lockKind && (
        <div className="gb-search-tabs" role="tablist">
          {TABS.map(({ key, label }) => (
            <button type="button" key={key} role="tab" aria-selected={kind === key} className={kind === key ? 'is-active' : ''} onClick={() => setKind(key)}>
              <CategoryIcon kind={key} size={24} /> <span>{label}</span>
            </button>
          ))}
        </div>
      )}

      {isStays && (
        <div className="gb-stay-toggle" role="tablist" aria-label="Stay type">
          <button type="button" role="tab" aria-selected={isHourly} className={isHourly ? 'is-active' : ''} onClick={() => setStay('hourly')}>
            <IconClock size={16} /> Hourly
          </button>
          <button type="button" role="tab" aria-selected={!isHourly} className={!isHourly ? 'is-active' : ''} onClick={() => setStay('overnight')}>
            <IconMoon size={16} /> Overnight
          </button>
        </div>
      )}

      <div className={`gb-search-fields ${isStays ? 'has-dates' : ''}`}>
        {/* Where */}
        <div className={`gb-field gb-field-where ${open === 'where' ? 'is-open' : ''}`}>
          <button type="button" className="gb-field-btn" onClick={() => setOpen(open === 'where' ? null : 'where')} aria-expanded={open === 'where'}>
            <span className="gb-field-label">{isStays ? 'City, area or hotel' : 'City or area'}</span>
            <span className={`gb-field-value ${!whereMain ? 'is-empty' : ''}`}>{whereMain || 'Where to?'}</span>
            <span className="gb-field-sub">{whereSub}</span>
          </button>
          {open === 'where' && (
            <div className="gb-pop gb-pop-where">
              <label className="gb-pop-search">
                <IconSearch size={17} />
                <input
                  ref={whereInput}
                  type="text"
                  value={query}
                  placeholder="Search city, area or landmark"
                  onChange={(e) => { setQuery(e.target.value); setTyping(true); }}
                  autoComplete="off"
                />
              </label>
              <button type="button" className="gb-pop-row gb-pop-near" onClick={useMyLocation} disabled={locating}>
                <IconNavigate size={16} /> {locating ? 'Finding your location…' : 'Near me'}
              </button>
              {predictions.length > 0 && (
                <div className="gb-pop-group">
                  <span>Places</span>
                  {predictions.map((p) => (
                    <button type="button" key={p.place_id} className="gb-pop-row" onClick={() => pickPlace(p)}>
                      <IconPin size={15} />
                      <span><b>{p.structured_formatting?.main_text || p.description}</b>{p.structured_formatting?.secondary_text && <small>{p.structured_formatting.secondary_text}</small>}</span>
                    </button>
                  ))}
                </div>
              )}
              {cityMatches.length > 0 && (
                <div className="gb-pop-group">
                  <span>Cities on WeOtp</span>
                  {cityMatches.map((c) => (
                    <button type="button" key={c} className="gb-pop-row" onClick={() => choose({ city: c, state: '', country: '', lat: null, lng: null, area: '', near: false })}>
                      <IconHotel size={15} /> <span><b>{c.split(',')[0]}</b><small>{c.split(',').slice(1).join(',').trim()}</small></span>
                    </button>
                  ))}
                </div>
              )}
              {!query.trim() && destinations.length > 0 && (
                <div className="gb-pop-group">
                  <span>Popular destinations</span>
                  {destinations.slice(0, 8).map((d) => (
                    <button type="button" key={`${d.city}-${d.state}`} className="gb-pop-row" onClick={() => choose({ city: [d.city, d.state, d.country].filter(Boolean).join(', '), state: d.state || '', country: d.country || '', lat: d.cityLatitude ?? null, lng: d.cityLongitude ?? null, area: '', near: false })}>
                      <IconPin size={15} /> <span><b>{d.city}</b><small>{[d.state, d.country].filter(Boolean).join(', ')}</small></span>
                    </button>
                  ))}
                </div>
              )}
              {query.trim().length >= 3 && predictions.length === 0 && cityMatches.length === 0 && (
                <div className="gb-pop-none">Keep typing, or pick a city from the suggestions.</div>
              )}
            </div>
          )}
        </div>

        {/* Check-in / check-out (overnight) or check-in date + time (hourly) */}
        {isStays && (
          <>
            <div className={`gb-field gb-field-date ${open === 'in' ? 'is-open' : ''}`}>
              <button type="button" className="gb-field-btn" onClick={() => setOpen(open === 'in' ? null : 'in')} aria-expanded={open === 'in'}>
                <span className="gb-field-label">{isHourly ? 'Check-in date' : 'Check-in'}</span>
                <span className="gb-field-value"><b>{inParts.day}</b> {inParts.month}</span>
                <span className="gb-field-sub">{inParts.weekday}</span>
              </button>
              {open === 'in' && (
                <div className="gb-pop gb-pop-cal">
                  <Calendar start={search.checkIn} end={isHourly ? undefined : search.checkOut} min={todayKey()} months={twoMonths ? 2 : 1} onPick={pickCheckIn} />
                </div>
              )}
            </div>

            {isHourly ? (
              <div className={`gb-field gb-field-time ${open === 'time' ? 'is-open' : ''}`}>
                <button type="button" className="gb-field-btn" onClick={() => setOpen(open === 'time' ? null : 'time')} aria-expanded={open === 'time'}>
                  <span className="gb-field-label">Check-in time</span>
                  <span className="gb-field-value">{formatTime(search.time)}</span>
                  <span className="gb-field-sub">Pick hours on the hotel</span>
                </button>
                {open === 'time' && (
                  <div className="gb-pop gb-pop-time" ref={timePop}>
                    {TIME_SLOTS.map((slot) => {
                      const past = search.checkIn === todayKey() && slot <= nowTime();
                      return (
                        <button type="button" key={slot} className={`gb-pop-row gb-time-row ${slot === search.time ? 'is-active' : ''}`} disabled={past} onClick={() => pickTime(slot)}>
                          {formatTime(slot)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className={`gb-field gb-field-date ${open === 'out' ? 'is-open' : ''}`}>
                <button type="button" className="gb-field-btn" onClick={() => setOpen(open === 'out' ? null : 'out')} aria-expanded={open === 'out'}>
                  <span className="gb-field-label">Check-out</span>
                  <span className="gb-field-value"><b>{outParts.day}</b> {outParts.month}</span>
                  <span className="gb-field-sub">{outParts.weekday} · {nights} night{nights === 1 ? '' : 's'}</span>
                </button>
                {open === 'out' && (
                  <div className="gb-pop gb-pop-cal">
                    <Calendar start={search.checkIn} end={search.checkOut} min={addDays(search.checkIn, 1)} months={twoMonths ? 2 : 1} onPick={pickCheckOut} />
                  </div>
                )}
              </div>
            )}

            <div className={`gb-field gb-field-guests ${open === 'guests' ? 'is-open' : ''}`}>
              <button type="button" className="gb-field-btn" onClick={() => setOpen(open === 'guests' ? null : 'guests')} aria-expanded={open === 'guests'}>
                <span className="gb-field-label">Rooms & guests</span>
                <span className="gb-field-value"><b>{search.rooms}</b> Room{search.rooms === 1 ? '' : 's'}, <b>{guests}</b> Guest{guests === 1 ? '' : 's'}</span>
                <span className="gb-field-sub">{search.adults} adult{search.adults === 1 ? '' : 's'}{search.children ? `, ${search.children} child${search.children === 1 ? '' : 'ren'}` : ''}</span>
              </button>
              {open === 'guests' && (
                <div className="gb-pop gb-pop-guests">
                  <div className="gb-guest-row"><span>Rooms</span><Stepper value={search.rooms} min={1} max={10} label="rooms" onChange={(v) => setSearch({ ...search, rooms: v })} /></div>
                  <div className="gb-guest-row"><span>Adults<small>12 years & above</small></span><Stepper value={search.adults} min={1} max={20} label="adults" onChange={(v) => setSearch({ ...search, adults: v })} /></div>
                  <div className="gb-guest-row"><span>Children<small>Below 12 years</small></span><Stepper value={search.children} min={0} max={10} label="children" onChange={(v) => setSearch({ ...search, children: v })} /></div>
                  <button type="button" className="gb-btn gb-btn-primary gb-btn-block" onClick={() => setOpen(null)}>Done</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="gb-search-action">
        <button type="submit" className="gb-btn gb-btn-primary gb-search-submit">
          <IconSearch size={20} /> Search {KINDS[kind].label}
        </button>
      </div>
      {error && <div className="gb-search-error">{error}</div>}
    </form>
  );
};

export default SearchBar;
